import React, { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography
} from "@mui/material";

interface Props {
  open: boolean;
  onClose: () => void;
}

const MAX_VISIBLE_CHIPS = 2;

export const InviteMembersModal: React.FC<Props> = ({ open, onClose }) => {
  // All emails typed during this session — cleared on modal close, never shrinks
  const [allEmails, setAllEmails] = useState<string[]>([]);
  // Subset currently "active" — shown as chips, sent to backend on Invite
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [message, setMessage] = useState("");
  const [expanded, setExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expanded]);

  const addEmail = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed) {
      setAllEmails((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
      setSelectedEmails((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    }
    setInputValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      addEmail(inputValue);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    addEmail(e.clipboardData.getData("text"));
  };

  const handleDeleteChip = (email: string) => {
    setSelectedEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleToggleFromList = (email: string) => {
    setSelectedEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
  };

  const handleClose = () => {
    setAllEmails([]);
    setSelectedEmails([]);
    setInputValue("");
    setMessage("");
    setExpanded(false);
    onClose();
  };

  const visibleChips = expanded ? selectedEmails : selectedEmails.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = selectedEmails.length - MAX_VISIBLE_CHIPS;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: "520px"
        }
      }}
    >
      <DialogTitle sx={{ p: "16px 24px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#071F35" }}>Invite team members</Typography>
          <IconButton onClick={handleClose} size="sm">
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Divider sx={{ mt: "8px", borderColor: "#DBE8F5" }} />
      </DialogTitle>

      <DialogContent sx={{ p: "8px 24px 24px" }}>
        <Typography sx={{ color: "#496682", mb: "10px" }}>Add emails of your team members</Typography>

        <Box ref={dropdownRef} sx={{ position: "relative" }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={selectedEmails}
            inputValue={inputValue}
            onInputChange={(_, val, reason) => {
              if (reason !== "reset") setInputValue(val);
            }}
            onChange={(_, newValue) => {
              setSelectedEmails(newValue as string[]);
            }}
            open={false}
            disableClearable
            renderTags={() => (
              <>
                {visibleChips.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleDeleteChip(email)}
                    size="small"
                    sx={{
                      mr: "4px",
                      backgroundColor: "#F0F4F8",
                      color: "#071F35",
                      fontSize: "13px",
                      "& .MuiChip-deleteIcon": { color: "#496682" }
                    }}
                  />
                ))}
                {!expanded && hiddenCount > 0 && (
                  <Chip
                    label={`+${hiddenCount}`}
                    size="small"
                    sx={{
                      mr: "4px",
                      backgroundColor: "#E5E8EF",
                      color: "#496682",
                      fontSize: "12px",
                      fontWeight: 600
                    }}
                  />
                )}
              </>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email"
                variant="outlined"
                size="small"
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: handleKeyDown,
                  onPaste: handlePaste
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment:
                    allEmails.length > 0 ? (
                      <IconButton
                        size="sm"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExpanded((prev) => !prev);
                        }}
                        sx={{ color: "#496682", p: "2px", flexShrink: 0 }}
                      >
                        {expanded ? (
                          <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    ) : null
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    flexWrap: "wrap",
                    gap: "4px",
                    padding: "8px 14px",
                    alignItems: "center"
                  }
                }}
              />
            )}
          />

          {expanded && allEmails.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                zIndex: 10,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E8EF",
                borderRadius: "6px",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                overflow: "hidden"
              }}
            >
              {allEmails.map((email) => {
                const isSelected = selectedEmails.includes(email);
                return (
                  <Box
                    key={email}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleToggleFromList(email);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: "14px",
                      py: "8px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#F0F4F8" : "#FFFFFF",
                      "&:hover": { backgroundColor: "#F8F9FD" },
                      "&:not(:last-child)": { borderBottom: "1px solid #E5E8EF" }
                    }}
                  >
                    <Typography sx={{ fontSize: "13px", color: "#071F35" }}>{email}</Typography>
                    {isSelected && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#4FC3A1",
                          flexShrink: 0
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <TextField
          multiline
          fullWidth
          placeholder="Introduction text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          minRows={4}
          sx={{
            mt: "12px",
            "& .MuiOutlinedInput-root": {
              height: "100px",
              alignItems: "flex-start"
            }
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: "16px 24px", gap: "12px" }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={selectedEmails.length === 0}>
          Invite
        </Button>
      </DialogActions>
    </Dialog>
  );
};
